import { useSetAtom, useAtomValue, atom } from 'jotai';
import { useCallback } from 'react';

import type { MarketplaceAcquisitionState } from '../components/Cards/marketplace-card-cta';

const acquisitionsByListingAtom = atom<
  Record<string, MarketplaceAcquisitionState>
>({});

// Session-scoped memory of what happened to a marketplace listing, keyed by
// listing id. Global (atom) because the readers and the writers are not in one
// tree: `inLibrary`/`restorable` travel inside list rows, and the preview modal
// COPIES them into component state when a card is clicked. React Query can
// refresh the rows; it cannot reach that copy, so the modal's footer kept
// offering an acquisition the session had already completed and every further
// click answered `contentAlreadyInLibrary`.
//
// Not a substitute for the query cache: it records only what THIS session
// watched succeed, and a reload correctly starts empty because the rows
// themselves then carry the truth.
export const useMarketplaceAcquisitions = () =>
  useAtomValue(acquisitionsByListingAtom);

// The ledger belongs to the SESSION that wrote it, and signing out does not
// reload the page — left standing it would tell the next login on this tab that
// content it does not own is already "In saved". `AuthProvider.signOutAsync` is
// the chokepoint that drops it, next to the tenant override and the query cache.
export const useResetMarketplaceAcquisitions = () => {
  const setAcquisitions = useSetAtom(acquisitionsByListingAtom);

  return useCallback(() => setAcquisitions({}), [setAcquisitions]);
};

// Deliberately separate from the reader: the writers are the add/remove
// mutation hooks, which are mounted once per card in a grid — subscribing them
// to the ledger's VALUE would re-render every card in the list on each write.
export const useRecordMarketplaceAcquisition = () => {
  const setAcquisitions = useSetAtom(acquisitionsByListingAtom);

  return useCallback(
    (listingId: string, state: MarketplaceAcquisitionState) =>
      setAcquisitions((previous) => ({ ...previous, [listingId]: state })),
    [setAcquisitions],
  );
};
